import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addTaxExemption, downloadTaxCertificate, listTaxExemptions, removeTaxExemption } from '@/lib/partnerTax';
import styles from '@/pages/partner-jobs/PartnerJobs.module.css';

/**
 * Resale and exemption certificates, once per state (Warren, final ruling 5, 21 October 2026, night). Uploading one makes
 * the partner's own orders from EVI exempt from sales tax in that state through Stripe Tax; orders its customers pay are
 * taxed as usual. Shown only when the Api lets this login read them: the partner's account owner and EVI.
 */
export function TaxCertificatesCard() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['partner-tax-exemptions'], queryFn: listTaxExemptions, retry: false });
  const [state, setState] = useState(''), [expires, setExpires] = useState(''), [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false), [message, setMessage] = useState(''), [inputKey, setInputKey] = useState(0);
  if (!q.data) return null;
  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true); setMessage('');
    try { await fn(); await client.invalidateQueries({ queryKey: ['partner-tax-exemptions'] }); setMessage(done); }
    catch (err) { setMessage(err instanceof Error ? err.message : 'Not saved.'); }
    finally { setBusy(false); }
  };
  const upload = () => {
    if (!state) { setMessage('Choose the state the certificate is for.'); return; }
    if (!file) { setMessage('Attach the certificate.'); return; }
    void run(async () => { await addTaxExemption(file, { state, expiration_date: expires || null }); setFile(null); setExpires(''); setInputKey(k => k + 1); },
      `Your certificate for ${state} is on file. Your own orders shipped to ${state} are not charged sales tax.`);
  };
  return <section className={styles.card} aria-labelledby="tax-certificates">
    <div className={styles.cardHead}><h2 id="tax-certificates" className={styles.kicker}>Resale and exemption certificates</h2></div>
    <p className={styles.hint}>Upload your resale or exemption certificate once for each state. Your own orders from EVI are then not charged sales tax in that state. Orders your customers pay are taxed as usual. Only your account owner and EVI see these.</p>
    {q.data.exemptions.map(e => <div key={e.id} className={styles.slot}>
      <div><div className={styles.strong}>{e.state}</div>
        <span className={styles.hint}>From {e.effective_date}{e.expiration_date ? `, expires ${e.expiration_date}` : ''}. Uploaded by {e.uploaded_by}.</span></div>
      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className={styles.ghost} disabled={busy} onClick={() => void run(() => downloadTaxCertificate(e.id, e.certificate.filename), '')}>Download</button>
        <button type="button" className={styles.ghost} disabled={busy} onClick={() => void run(() => removeTaxExemption(e.id), `Your certificate for ${e.state} is removed. Your orders shipped there are taxed again.`)}>Remove</button>
      </span>
    </div>)}
    <div className={styles.grid2}>
      <label className={styles.field}><span>State</span>
        <select value={state} disabled={busy} onChange={ev => setState(ev.target.value)}>
          <option value="">Choose a state</option>
          {q.data.states.map(s => <option key={s} value={s}>{s}</option>)}
        </select></label>
      <label className={styles.field}><span>Expires (if it does)</span>
        <input type="date" value={expires} disabled={busy} onChange={ev => setExpires(ev.target.value)} /></label>
    </div>
    <label className={styles.field}><span>Certificate (PDF or a photo, under 2 MB)</span>
      <input key={inputKey} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" disabled={busy} onChange={ev => setFile(ev.target.files?.[0] ?? null)} /></label>
    <div><button type="button" className={styles.ghost} disabled={busy} onClick={upload}>{busy ? 'Saving' : 'Upload certificate'}</button></div>
    <p className={styles.message} role="status">{message}</p>
  </section>;
}
